import React, { useMemo, useState } from "react";

function OpeningBalanceCrossCheck({
    partyData = {},
    initialEnterValue = 0
}) {

    const [enterValue, setEnterValue] = useState(initialEnterValue);


    // =========================================================
    // GET OPENING BALANCE
    // =========================================================

    const getOpeningBalance = (party) => {

        if (!party) {
            return 0;
        }

        return Number(
            party.opening_balance ??
            party.openingBalance ??
            party.balance ??
            0
        );

    };


    // =========================================================
    // SUM OPENING BALANCE
    // =========================================================

    const getTotal = (items = []) => {

        if (!Array.isArray(items)) {
            return 0;
        }

        return items.reduce(
            (total, item) => {
                return total + getOpeningBalance(item);
            },
            0
        );

    };


    // =========================================================
    // PARTY TYPE TOTALS
    // =========================================================

    const totals = useMemo(() => {

        return {

            banks: getTotal(
                partyData.banks
            ),

            buyers: getTotal(
                partyData.buyers
            ),

            despatchos: getTotal(
                partyData.despatchos
            ),

            ports: getTotal(
                partyData.ports
            ),

            shippings: getTotal(
                partyData.shippings
            ),

            transporters: getTotal(
                partyData.transporters
            )

        };

    }, [partyData]);


    // =========================================================
    // GRAND TOTAL A
    // =========================================================

    const grandTotal = useMemo(() => {

        return (

            totals.banks +
            totals.buyers +
            totals.despatchos +
            totals.ports +
            totals.shippings +
            totals.transporters

        );

    }, [totals]);


    // =========================================================
    // A - B
    // =========================================================

    const difference =
        grandTotal - Number(enterValue || 0);


    const isMatched =
        Math.abs(difference) < 0.000001;


    // =========================================================
    // FORMAT CURRENCY
    // =========================================================

    const formatCurrency = (value) => {

        return new Intl.NumberFormat(
            "en-IN",
            {
                style: "currency",
                currency: "INR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        ).format(Number(value || 0));

    };


    // =========================================================
    // ROW
    // =========================================================

    const CrossCheckRow = ({
        number,
        partyType,
        value
    }) => {

        return (

            <tr>

                <td className="text-center">
                    {number}
                </td>

                <td>
                    <strong>
                        {partyType}
                    </strong>
                </td>

                <td className="text-end">
                    {formatCurrency(value)}
                </td>

            </tr>

        );

    };


    return (

        <div className="card border-0 shadow-sm mb-4">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="card-header bg-dark text-white">

                <div className="d-flex justify-content-between align-items-center">

                    <div>

                        <h5 className="mb-1">
                            Opening Balance Cross Check
                        </h5>

                        <small className="opacity-75">
                            All Party Types Opening Balance Verification
                        </small>

                    </div>

                </div>

            </div>


            {/* =================================================
                BODY
            ================================================= */}

            <div className="card-body p-0">

                <div className="table-responsive">

                    <table className="table table-bordered table-hover mb-0">

                        <thead className="table-light">

                            <tr>

                                <th
                                    style={{
                                        width: "70px"
                                    }}
                                    className="text-center"
                                >
                                    #
                                </th>

                                <th>
                                    Party Type
                                </th>

                                <th
                                    className="text-end"
                                    style={{
                                        width: "220px"
                                    }}
                                >
                                    Total Opening Balance
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            <CrossCheckRow
                                number="1"
                                partyType="Banks"
                                value={totals.banks}
                            />


                            <CrossCheckRow
                                number="2"
                                partyType="Buyers"
                                value={totals.buyers}
                            />


                            <CrossCheckRow
                                number="3"
                                partyType="Dispatcho"
                                value={totals.despatchos}
                            />


                            <CrossCheckRow
                                number="4"
                                partyType="Ports"
                                value={totals.ports}
                            />


                            <CrossCheckRow
                                number="5"
                                partyType="Shipping Lines"
                                value={totals.shippings}
                            />


                            <CrossCheckRow
                                number="6"
                                partyType="Transporters"
                                value={totals.transporters}
                            />


                            {/* =====================================
                                GRAND TOTAL A
                            ===================================== */}

                            <tr className="table-secondary">

                                <td
                                    colSpan="2"
                                    className="text-end"
                                >
                                    <strong>
                                        Grand Total A
                                    </strong>
                                </td>

                                <td className="text-end">

                                    <strong>
                                        {formatCurrency(
                                            grandTotal
                                        )}
                                    </strong>

                                </td>

                            </tr>


                            {/* =====================================
                                ENTER VALUE B
                            ===================================== */}

                            <tr>

                                <td
                                    colSpan="2"
                                    className="text-end"
                                >

                                    <strong>
                                        Enter Value B
                                    </strong>

                                </td>

                                <td>

                                    <input
                                        type="number"
                                        className="form-control text-end"
                                        value={enterValue}
                                        onChange={(e) =>
                                            setEnterValue(
                                                e.target.value
                                            )
                                        }
                                        step="0.01"
                                        placeholder="Enter value"
                                    />

                                </td>

                            </tr>


                            {/* =====================================
                                DIFFERENCE
                            ===================================== */}

                            <tr>

                                <td
                                    colSpan="2"
                                    className="text-end"
                                >

                                    <strong>
                                        A - B
                                    </strong>

                                </td>

                                <td
                                    className={
                                        isMatched
                                            ? "text-end text-success"
                                            : "text-end text-danger"
                                    }
                                >

                                    <strong>

                                        {formatCurrency(
                                            difference
                                        )}

                                    </strong>

                                </td>

                            </tr>


                            {/* =====================================
                                STATUS
                            ===================================== */}

                            <tr>

                                <td
                                    colSpan="2"
                                    className="text-end"
                                >

                                    <strong>
                                        Status
                                    </strong>

                                </td>

                                <td className="text-end">

                                    {isMatched ? (

                                        <span className="badge bg-success px-3 py-2">
                                            ✓ Matched
                                        </span>

                                    ) : (

                                        <span className="badge bg-danger px-3 py-2">
                                            ⚠ Difference
                                        </span>

                                    )}

                                </td>

                            </tr>

                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

}

export default OpeningBalanceCrossCheck;